
import React, { useState, useEffect } from "react";
import { Customer, Order } from "@/api/entities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Plus, 
  Search, 
  Users, 
  Mail, 
  MapPin,
  ShoppingCart,
  DollarSign
} from "lucide-react";

import CustomerTable from "../components/customers/CustomerTable";
import CreateCustomerModal from "../components/customers/CreateCustomerModal";
import EditCustomerModal from "../components/customers/EditCustomerModal";
import { useLocalization } from "../components/providers/LocalizationProvider";

export default function Customers() {
  const { currencySymbol } = useLocalization();
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterCustomers();
  }, [customers, searchTerm]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [customersData, ordersData] = await Promise.all([
        Customer.list('-created_date'),
        Order.list()
      ]);
      
      setCustomers(customersData);
      setOrders(ordersData);
    } catch (error) {
      console.error("Error loading data:", error);
    }
    setIsLoading(false);
  };

  const filterCustomers = () => {
    let filtered = customers;

    if (searchTerm) {
      filtered = filtered.filter(customer => 
        customer.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phone?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredCustomers(filtered);
  };

  const handleCustomerCreate = async (customerData) => {
    await Customer.create(customerData);
    await loadData();
    setShowCreateModal(false);
  };

  const handleCustomerUpdate = async (customerId, updates) => {
    await Customer.update(customerId, updates);
    await loadData();
    setShowEditModal(false);
    setSelectedCustomer(null);
  };

  const handleCustomerEdit = (customer) => {
    setSelectedCustomer(customer);
    setShowEditModal(true);
  };

  const getCustomerStats = (customer) => {
    const customerOrders = orders.filter(order => order.customer_id === customer.id);
    const totalOrders = customerOrders.length;
    const totalSpent = customerOrders.reduce((sum, order) => {
      const amount = parseFloat(order.total_amount) || 0;
      return sum + amount;
    }, 0);
    const lastOrderDate = customerOrders.length > 0 ? 
      Math.max(...customerOrders.map(order => new Date(order.created_date).getTime())) : null;

    return { totalOrders, totalSpent, lastOrderDate };
  };

  const getTotalStats = () => {
    const totalCustomers = customers.length;
    const totalRevenue = orders.reduce((sum, order) => {
      const amount = parseFloat(order.total_amount) || 0;
      return sum + amount;
    }, 0);
    const avgOrderValue = orders.length > 0 ? totalRevenue / orders.length : 0;
    const activeCustomers = customers.filter(customer => {
      const customerOrders = orders.filter(order => order.customer_id === customer.id);
      return customerOrders.length > 0;
    }).length;

    return { totalCustomers, totalRevenue, avgOrderValue, activeCustomers };
  };

  const stats = getTotalStats();

  return (
    <div className="min-h-screen gradient-bg p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Customer Management</h1>
            <p className="text-slate-600 font-medium mt-1">
              Manage your customer relationships and data
            </p>
          </div>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="bg-amber-600 hover:bg-amber-700 text-white font-medium px-6"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Customer
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="elegant-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Total Customers</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2">{stats.totalCustomers}</p>
                </div>
                <div className="p-3 rounded-xl bg-blue-50">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="elegant-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Active Customers</p>
                  <p className="text-3xl font-bold text-green-600 mt-2">{stats.activeCustomers}</p>
                </div>
                <div className="p-3 rounded-xl bg-green-50">
                  <ShoppingCart className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="elegant-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Total Revenue</p>
                  <p className="text-3xl font-bold text-amber-600 mt-2">{currencySymbol}{Number(stats.totalRevenue || 0).toFixed(0)}</p>
                </div>
                <div className="p-3 rounded-xl bg-amber-50">
                  <DollarSign className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="elegant-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Avg Order Value</p>
                  <p className="text-3xl font-bold text-purple-600 mt-2">{currencySymbol}{Number(stats.avgOrderValue || 0).toFixed(0)}</p>
                </div>
                <div className="p-3 rounded-xl bg-purple-50">
                  <DollarSign className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card className="elegant-card">
          <CardContent className="p-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search customers by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-slate-200 focus:border-amber-500 focus:ring-amber-500"
              />
            </div>
          </CardContent>
        </Card>

        {/* Customer Table */}
        <CustomerTable 
          customers={filteredCustomers}
          onCustomerEdit={handleCustomerEdit}
          getCustomerStats={getCustomerStats}
          isLoading={isLoading}
        />

        {/* Modals */}
        <CreateCustomerModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCustomerCreate}
        />

        <EditCustomerModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedCustomer(null);
          }}
          customer={selectedCustomer}
          onSubmit={handleCustomerUpdate}
        />
      </div>
    </div>
  );
}
